import { useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Image, FileText, Play, MapPin, Download } from "@phosphor-icons/react";

function mediaUrl(media_id) {
  const token = localStorage.getItem("wh_token");
  // append token via query fallback? Instead use fetch + blob below for secure auth
  return `${API_BASE}/media/${media_id}`;
}

export default function MediaBubble({ message, outgoing }) {
  const m = message.media || {};
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMedia = async () => {
    if (src || !m.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("wh_token");
      const r = await fetch(`${API_BASE}/media/${m.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      setSrc(URL.createObjectURL(blob));
    } finally {
      setLoading(false);
    }
  };

  if (message.location) {
    const { latitude, longitude, name, address } = message.location;
    const map = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`;
    return (
      <a
        href={map}
        target="_blank"
        rel="noreferrer"
        className="flex items-start gap-2 text-xs underline-offset-2 hover:underline"
      >
        <MapPin size={16} className="mt-0.5 shrink-0" />
        <span>
          <b>{name || "Location"}</b>
          {address && <div className="opacity-80">{address}</div>}
          <div className="font-mono text-[10px] opacity-60">
            {latitude?.toFixed?.(4)}, {longitude?.toFixed?.(4)}
          </div>
        </span>
      </a>
    );
  }

  if (m.kind === "image") {
    // auto-fetch on first render
    if (!src && !loading) fetchMedia();
    return (
      <div className="space-y-1">
        <div
          className="relative rounded-lg overflow-hidden border border-[var(--border)] cursor-pointer"
          onClick={() => src && window.open(src, "_blank")}
        >
          {src ? (
            <img src={src} alt="" className="max-w-full max-h-64 object-cover" />
          ) : (
            <div className="w-60 h-40 grid place-items-center bg-[var(--bg)] text-[var(--muted)]">
              <Image size={28} />
            </div>
          )}
        </div>
        {m.caption && <div className="text-sm">{m.caption}</div>}
      </div>
    );
  }

  if (m.kind === "audio" || m.voice) {
    if (!src && !loading) fetchMedia();
    return (
      <div className="flex items-center gap-2 min-w-[200px]">
        {src ? (
          <audio controls src={src} className="h-8 flex-1" />
        ) : (
          <div className="flex items-center gap-2 text-xs opacity-70">
            <Play size={14} /> Loading audio…
          </div>
        )}
      </div>
    );
  }

  if (m.kind === "video") {
    if (!src && !loading) fetchMedia();
    return src ? (
      <video controls src={src} className="max-w-full max-h-64 rounded-lg" />
    ) : (
      <div className="text-xs opacity-70">Loading video…</div>
    );
  }

  if (m.kind === "document") {
    const download = async () => {
      const token = localStorage.getItem("wh_token");
      const r = await fetch(`${API_BASE}/media/${m.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = m.filename || "file";
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <button
        type="button"
        onClick={download}
        className={`flex items-center gap-2 text-left px-3 py-2 rounded-lg border ${
          outgoing
            ? "border-[var(--bg)]/20 hover:bg-[var(--bg)]/10"
            : "border-[var(--border)] hover:bg-[var(--bg)]"
        }`}
      >
        <FileText size={22} />
        <div className="min-w-0">
          <div className="text-xs font-medium truncate max-w-[200px]">
            {m.filename || "document"}
          </div>
          <div className="text-[10px] opacity-60 flex items-center gap-1">
            <Download size={10} /> Download
          </div>
        </div>
      </button>
    );
  }

  return null;
}
